import { hasMenu, isAdmin, MenuAction } from './access';

/**
 * Every functional area of the app. Route guards and in-page affordances resolve access
 * through `MODULE_MENUS`: each key belongs to one sub-menu of the role-wise menu (V30), and
 * optionally needs an action on it. Which roles have which sub-menus is no longer written
 * here: an admin assigns them on Roles & Menu Access, and `/api/my/access` reports them.
 *
 * The API enforces the same grants for payroll, attendance and accounting; elsewhere it
 * still applies its own role rules.
 */
export const MODULES = {
  HOME: 'home',
  EMPLOYEES: 'employees',
  USERS: 'users',
  HR_ADMIN: 'hrAdmin',
  DEPARTMENTS: 'departments',
  ROLES: 'roles',
  RECRUITMENT: 'recruitment',
  CANDIDATES: 'candidates',
  LEAVES: 'leaves',
  LEAVE_APPROVALS: 'leaveApprovals',
  ASSIGN_WORK: 'assignWork',
  PROJECTS: 'projects',
  WORK_JOB_REPORT: 'workJobReport',
  WORK_JOB_EDIT: 'workJobEdit',
  /** V36 — the admin's queue of quotations with a work order, waiting for approval. */
  QUOTATION_APPROVALS: 'quotationApprovals',
  SITE_VISITS: 'siteVisits',
  REIMBURSEMENTS: 'reimbursements',
  REIMBURSEMENT_APPROVALS: 'reimbursementApprovals',
  BILLS: 'bills',
  OUTSTANDING: 'outstanding',
  PAYMENTS: 'payments',
  SALARY: 'salary',
  NEWS: 'news',
  WEBSITE: 'website',
  FILES: 'files',
  REFERENCE_DATA: 'referenceData',
  CONTACT_MASTERS: 'contactMasters',
  COMPANY_MASTER: 'companyMaster',
  /** Adding or changing companies: stays with admin (the API pins it to ROLE_ADMIN). */
  COMPANY_EDIT: 'companyEdit',
  FEEDBACK: 'feedback',
  LETTERS: 'letters',
  /** Issuing letters and certificates to other people. */
  LETTERS_ISSUE: 'lettersIssue',
  TASKS: 'tasks',
  REASSIGNMENTS: 'reassignments',
  MY_WORK: 'myWork',
  MY_TIMESHEET: 'myTimesheet',
  TEAM_TIMESHEETS: 'teamTimesheets',
  WORKLOAD: 'workload',
  MY_ATTENDANCE: 'myAttendance',
  MY_SALARY: 'mySalary',
  ATTENDANCE_REGISTER: 'attendanceRegister',
  HOLIDAYS: 'holidays',
  PAYROLL: 'payroll',
  PAY_PROFILES: 'payProfiles',
  PAYROLL_SETUP: 'payrollSetup',
} as const;

export type ModuleKey = (typeof MODULES)[keyof typeof MODULES];

/** The sub-menu (and action, when more than VIEW is needed) behind each module key. */
export const MODULE_MENUS: Record<ModuleKey, { menu: string; action?: MenuAction; adminOnly?: boolean }> = {
  [MODULES.HOME]: { menu: 'home.home' },

  [MODULES.MY_WORK]: { menu: 'my.work' },
  [MODULES.MY_TIMESHEET]: { menu: 'my.timesheet' },
  [MODULES.MY_ATTENDANCE]: { menu: 'my.attendance' },
  [MODULES.LEAVES]: { menu: 'my.leaves' },
  [MODULES.MY_SALARY]: { menu: 'my.salary' },
  [MODULES.REIMBURSEMENTS]: { menu: 'my.reimbursements' },

  [MODULES.PROJECTS]: { menu: 'projects.projects' },
  [MODULES.WORK_JOB_EDIT]: { menu: 'projects.projects', action: 'EDIT' },
  [MODULES.ASSIGN_WORK]: { menu: 'projects.projects', action: 'EDIT' },
  [MODULES.SITE_VISITS]: { menu: 'projects.site-visits' },
  [MODULES.WORK_JOB_REPORT]: { menu: 'projects.report' },
  [MODULES.QUOTATION_APPROVALS]: { menu: 'projects.quotation-approvals', adminOnly: true },

  [MODULES.TASKS]: { menu: 'tasks.tasks' },
  [MODULES.REASSIGNMENTS]: { menu: 'tasks.reassignments' },
  [MODULES.WORKLOAD]: { menu: 'tasks.workload' },
  [MODULES.TEAM_TIMESHEETS]: { menu: 'tasks.team-timesheets' },

  [MODULES.EMPLOYEES]: { menu: 'hr.employees' },
  [MODULES.HR_ADMIN]: { menu: 'hr.employees' },
  [MODULES.DEPARTMENTS]: { menu: 'hr.departments' },
  [MODULES.ATTENDANCE_REGISTER]: { menu: 'hr.attendance-register' },
  [MODULES.LEAVE_APPROVALS]: { menu: 'hr.leave-approvals' },
  [MODULES.HOLIDAYS]: { menu: 'hr.holidays' },
  [MODULES.LETTERS]: { menu: 'hr.letters' },
  [MODULES.LETTERS_ISSUE]: { menu: 'hr.letters', action: 'CREATE' },
  [MODULES.FEEDBACK]: { menu: 'hr.discipline' },

  [MODULES.RECRUITMENT]: { menu: 'recruitment.jobs' },
  [MODULES.CANDIDATES]: { menu: 'recruitment.candidates' },

  [MODULES.PAYROLL]: { menu: 'payroll.processing' },
  [MODULES.SALARY]: { menu: 'payroll.processing' },
  [MODULES.PAY_PROFILES]: { menu: 'payroll.profiles' },
  [MODULES.PAYROLL_SETUP]: { menu: 'payroll.rules' },

  [MODULES.BILLS]: { menu: 'billing.bills' },
  [MODULES.OUTSTANDING]: { menu: 'billing.outstanding' },
  [MODULES.PAYMENTS]: { menu: 'billing.payments' },
  [MODULES.REIMBURSEMENT_APPROVALS]: { menu: 'billing.reimbursement-approvals' },

  [MODULES.NEWS]: { menu: 'website.news' },
  [MODULES.WEBSITE]: { menu: 'website.contact' },

  [MODULES.COMPANY_MASTER]: { menu: 'masters.company' },
  [MODULES.COMPANY_EDIT]: { menu: 'masters.company', adminOnly: true },
  [MODULES.CONTACT_MASTERS]: { menu: 'masters.contacts' },
  [MODULES.REFERENCE_DATA]: { menu: 'masters.reference' },

  [MODULES.USERS]: { menu: 'admin.users' },
  [MODULES.ROLES]: { menu: 'admin.roles' },
  [MODULES.FILES]: { menu: 'admin.files' },
};

export function menuFor(moduleKey: ModuleKey): { menu: string; action: MenuAction } {
  const m = MODULE_MENUS[moduleKey];
  // An admin-only key resolves to an admin-only sub-menu, which only admin ever holds.
  if (m.adminOnly) return { menu: 'admin.users', action: 'VIEW' };
  return { menu: m.menu, action: m.action ?? 'VIEW' };
}

/**
 * True when the signed-in user may use `moduleKey`. The `role` argument is kept so existing
 * call sites read the same, but the answer comes from the role's menu grants.
 */
export function canAccess(_role: string | null | undefined, moduleKey: ModuleKey): boolean {
  const m = MODULE_MENUS[moduleKey];
  if (!m) return false;
  if (m.adminOnly) return isAdmin();
  return hasMenu(m.menu, m.action ?? 'VIEW');
}

/** @deprecated Use `canAccess(role, MODULES.…)`; kept for the legacy salary board. */
export function isPeopleAdmin(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'hr';
}
